import { ChangeDetectionStrategy, Component, OnInit, TemplateRef, ViewChild, computed, signal } from '@angular/core';
import { ListItem } from 'lib/ui/list/index.js';
import { IImage } from 'lib/ui/ui-models.js';
import { map } from 'rxjs';
import { UserApi } from '../../../../../api/user/user-api.js';
import { TaskDb } from '../../../../../lib/cosmos/task/taskDb.js';
import { UserDb } from '../../../../../lib/cosmos/user/userDb.js';
import { guidHelper } from '../../../../../lib/helpers/guid-helper.class.js';
import { IButton } from '../../../../../lib/ui/buttons/index.js';
import { IDialogButtonsContext, SelectionDialogItem } from '../../../../../lib/ui/dialog/index.js';
import { TypeCoreComponent } from '../../../pages/type-routes/type-core.component.js';

interface IExtendedTask extends UserDb.IUserAssignedTask {
	name: string;
	description: string | undefined;
	buttons: IButton[];
	avatar: IImage | undefined;
}

@Component({
	selector: 'mod-client-task.',
	templateUrl: './client-tasks.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class ClientTasksComponent extends TypeCoreComponent<UserDb.IUserItem> implements OnInit {
	@ViewChild('newTaskTemplate', { static: true }) newTaskTemplate!: TemplateRef<any>;

	public readonly enableEdit = computed<boolean>(() => {
		if (this.authUser()?.userType === 'trainer') {
			return true;
		}
		return false;
	});

	public readonly allTasks = signal<readonly TaskDb.ITaskItem[] | undefined>(undefined);
	public readonly assignedTasks = signal<readonly IExtendedTask[] | undefined>(undefined);

	public readonly assignedTasksListItems = computed<readonly ListItem<IExtendedTask>[] | undefined>(() => {
		return (
			this.assignedTasks()?.map<ListItem<IExtendedTask>>((m) => {
				return {
					data: m,
					id: m.id,
					title: m.name,
					subtitle: m.description,
					buttons: m.buttons,
					avatar: m.avatar
				};
			}) ?? undefined
		);
	});

	public readonly mainButtons = computed<IButton[]>(() => {
		const buttons: IButton[] = [
			{
				title: 'Assign task',
				buttonType: 'primary',
				buttonSize: 'default',
				click: () => {
					this.openSelectTask();
				}
			}
		];

		return buttons;
	});

	public readonly availableTasksForAssignment = computed<readonly TaskDb.ITaskItem[]>(() => {
		const allTasks = this.allTasks();
		const assignedTasks = this.assignedTasks();

		if (!allTasks || !assignedTasks) {
			return [];
		}

		return allTasks.filter((task) => {
			if (assignedTasks.find((m) => m.taskId === task.id)) {
				return false;
			}
			return true;
		});
	});

	constructor() {
		super();

		this.dataInit = () => {
			this.initTasks();
		};
	}

	handleSaveTasks(data: readonly UserDb.IUserAssignedTask[]): void {
		this.saveAssignedTasks(data);
	}

	handleCreateTask(task: TaskDb.ITaskItem): void {
		this.allTasks.update((allTasks) => {
			return [...(allTasks ?? []), task];
		});

		this.dependencies.dialogService.closeAllDialogs();
		this.assignTask(task);
	}

	private openCreateCustomTaskDialog(): void {
		this.dependencies.dialogService.openDialog({
			title: 'Create task',
			subtitle: 'You may create as many custom tasks as you need',
			size: 'large',
			template: () => this.newTaskTemplate
		});
	}

	private getSelectTaskDialogButtons(context: IDialogButtonsContext): IButton[] {
		return [
			{
				buttonSize: 'default',
				buttonType: 'basic',
				title: 'Create task',
				icon: 'add',
				click: () => {
					context.closeDialog();
					this.openCreateCustomTaskDialog();
				}
			}
		];
	}

	private updateAssignedTasks(): void {
		const sharedData = this.sharedData();
		if (!sharedData) {
			return;
		}
		const allTasks = this.allTasks();

		const currentlyAssignedTasks = sharedData.data.assignedTasks ?? [];

		this.assignedTasks.set(
			currentlyAssignedTasks.map((assignedTask) => {
				const linkedTask = allTasks?.find((m) => m.id === assignedTask.taskId);

				const extendedItem: IExtendedTask = {
					...assignedTask,
					description: linkedTask?.description,
					avatar: linkedTask?.avatar?.[0]?.image,
					name: super.getRequiredItemName(linkedTask?.system.name),
					buttons: [
						{
							iconOnly: true,
							icon: 'remove',
							title: `Remove`,
							confirm: {
								confirmMode: 'delete',
								text: `Are you sure to remove this task from client?`
							},
							click: () => {
								this.removeTask(assignedTask.id);
							}
						}
					]
				};

				return extendedItem;
			})
		);
	}

	private initTasks(): void {
		if (this.authUser()?.userType === 'trainer') {
			super.subscribeToObservable(
				this.dependencies.httpService
					.get<readonly TaskDb.ITaskItem[]>((builder) =>
						builder.withCustomApiRoute('task', 'getTasks').build()
					)
					.pipe(
						map((response) => {
							this.allTasks.set(response.data);
							this.updateAssignedTasks();
						})
					)
			);
		} else {
			const tasks: readonly TaskDb.ITaskItem[] = super.getLinkedItemsByType('Task');
			this.allTasks.set(tasks);
			this.updateAssignedTasks();
		}
	}

	private openUnassignTask(): void {
		const assignedTasks = this.assignedTasks();
		if (!assignedTasks) {
			return;
		}
		this.dependencies.dialogService.openSelectionDialog({
			title: `Select task to unassign`,
			items: assignedTasks.map((task) => {
				const selectionItem: SelectionDialogItem<IExtendedTask> = {
					data: task,
					id: task.id,
					title: task.name
				};

				return selectionItem;
			}),
			select: (item) => {
				this.dependencies.dialogService.openConfirmationDialog({
					text: `Are you sure to unassign the '${item.name}' task?`,
					confirm: () => {
						this.removeTask(item.id);
					}
				});
			}
		});
	}

	private openSelectTask(): void {
		this.dependencies.dialogService.openDialogWithSections({
			dialogId: 'openSelectTask',
			size: 'default',
			sections: [
				{
					id: 'allTasks',
					name: 'All tasks',
					dialog: (builder) => {
						return builder.selectionDialog({
							title: `Select task`,
							buttons: (context) => this.getSelectTaskDialogButtons(context),
							items: this.availableTasksForAssignment().map((task) => {
								const selectionItem: SelectionDialogItem<TaskDb.ITaskItem> = {
									data: task,
									id: task.id,
									title: task.system.name,
									subtitle: task.description,
									avatar: task.avatar?.[0]?.image
								};

								return selectionItem;
							}),
							select: (item) => {
								this.assignTask(item);
							}
						});
					}
				},
				{
					id: 'custom',
					name: 'Custom tasks',
					dialog: (builder) => {
						return builder.selectionDialog({
							title: `Select task`,
							buttons: (context) => this.getSelectTaskDialogButtons(context),
							items: this.availableTasksForAssignment()
								.filter((m) => !m.system.isGlobal)
								.map((task) => {
									const selectionItem: SelectionDialogItem<TaskDb.ITaskItem> = {
										data: task,
										id: task.id,
										title: task.system.name,
										subtitle: task.description,
										avatar: task.avatar?.[0]?.image
									};

									return selectionItem;
								}),
							select: (item) => {
								this.assignTask(item);
							}
						});
					}
				}
			]
		});
	}

	private saveAssignedTasks(assignedTasks: readonly UserDb.IUserAssignedTask[]): void {
		const sharedData = this.sharedData();

		if (!sharedData) {
			return;
		}

		sharedData.data.assignedTasks = assignedTasks;
		this.updateAssignedTasks();

		super.subscribeToObservable(
			this.dependencies.httpService
				.post<UserDb.IUserItem, UserApi.IEditTasksRequest>((builder) =>
					builder
						.withCustomApiRoute('user', 'editTasks')
						.withId(sharedData.data.id)
						.withData({
							tasks: assignedTasks
						})
						.build()
				)
				.pipe(
					map((response) => {
						// update user
						super.updateSharedData({
							data: response.data,
							linkedItems: response.linkedItems
						});

						super.notifySaved('Task');
					})
				)
		);
	}

	private removeTask(taskId: string): void {
		const sharedData = this.sharedData();

		if (!sharedData) {
			return;
		}

		this.saveAssignedTasks((sharedData.data.assignedTasks ?? []).filter((m) => m.id !== taskId));
	}

	private assignTask(task: TaskDb.ITaskItem): void {
		const sharedData = this.sharedData();

		if (!sharedData) {
			return;
		}

		this.saveAssignedTasks([
			...(sharedData.data.assignedTasks ?? []),
			{ id: guidHelper.shortGuid(), taskId: task.id }
		]);
	}
}
